type LoadingBarBase = {
  trackClassName?: string
  className?: string
}

/**
 * The two modes are exclusive. Determinate needs a percentage and reports it to
 * assistive tech; indeterminate has none and must not invent one, so `progress`
 * is not merely optional there — it is unavailable.
 */
type LoadingBarProps = LoadingBarBase &
  (
    | {
        indeterminate: true
        /** Names the work for screen readers, which have no percentage to read. */
        label?: string
        progress?: never
      }
    | {
        indeterminate?: false
        progress: number
        label?: never
      }
  )

export default function LoadingBar(props: LoadingBarProps) {
  const { trackClassName, className } = props
  const track = `w-full bg-gray-200 rounded-full overflow-hidden ${trackClassName ?? 'h-1'}`

  // An indeterminate progressbar is one with no aria-valuenow, so the value
  // attributes below are omitted rather than set to 0.
  if (props.indeterminate) {
    return (
      <div className={className} role="progressbar" aria-label={props.label ?? 'Working'}>
        <div className={track}>
          <div className="indeterminate-bar h-full w-1/4 bg-brand-500 rounded-full" />
        </div>
      </div>
    )
  }

  const pct = Math.min(100, Math.max(0, props.progress))
  return (
    <div
      className={className}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className={track}>
        <div
          className="h-full bg-brand-500 transition-[width] duration-300 ease-out rounded-full"
          style={{ width: `${pct}%` }}
        />
      </div>
      {pct > 0 && pct < 100 && (
        <div className="mt-1 text-xs font-medium text-gray-500 text-right">{Math.round(pct)}%</div>
      )}
    </div>
  )
}
